import React from 'react';
import Card from './Card';
import Skeleton from './Skeleton';

/**
 * Data table that renders a real <table> on md+ screens and a card list on
 * phones. Column config:
 *   { key, header, render?, priority?, align?, numeric?, cardLabel? }
 * priority: 1 = prominent on mobile card (default), 2 = label/value row on
 * mobile, 3 = desktop only. align: 'start' | 'end' | 'center'.
 */
export default function ResponsiveTable({
  columns = [],
  data: rawData = [],
  rowKey = 'id',
  onRowClick,
  loading = false,
  empty = null,
  cardTitle,
  cardActions,
  footer,
  // Bottom totals line: { [colKey]: value }. Rendered column-aligned in a
  // <tfoot> on desktop and as a summary card on mobile. A column's own
  // render() formats its total (called with the totals object as the "row").
  totals = null,
  totalsLabel = '',
  // Row-level treatments (void=dimmed+struck, reversed=strike, low-stock=red
  // wash, <=2-star=red wash per the tenant redesign). Applied to both the
  // desktop <tr> and the mobile card; null/undefined return = no extra class.
  rowClassName,
  className = '',
  // Hosts embedded in a narrow fixed-width column (e.g. the job-card
  // financial sidebar, 290-320px) must not get the 5-column desktop
  // <table> just because the VIEWPORT is md+ — Tailwind breakpoints see
  // the screen, not the container. forceCards renders the stacked card
  // layout at every width. ponytail: container queries would remove the
  // prop; adopt @container when the Tailwind config enables it.
  forceCards = false,
}) {
  const getKey = (row, i) => (typeof rowKey === 'function' ? rowKey(row, i) : row[rowKey] ?? i);
  const rowExtra = (row, i) => (rowClassName && rowClassName(row, i)) || '';
  const cell = (col, row, i) => (col.render ? col.render(row, i) : row[col.key]);
  const hasTotal = (col) => totals && Object.prototype.hasOwnProperty.call(totals, col.key);
  const totalCell = (col) => (col.render ? col.render(totals) : totals[col.key]);
  const totalCols = totals ? columns.filter(hasTotal) : [];
  const alignClass = (col) =>
    col.align === 'end' ? 'text-end' : col.align === 'center' ? 'text-center' : 'text-start';

  if (loading) {
    return (
      <>
        {!forceCards && (
          <div className="hidden md:block">
            <Skeleton.TableRows rows={5} cols={Math.min(columns.length, 5)} />
          </div>
        )}
        <div className={forceCards ? '' : 'md:hidden'}>
          <Skeleton.Card count={3} />
        </div>
      </>
    );
  }

  // One malformed row must not take the page down with it.
  //
  // Every column's render() is handed the row unguarded, so a null or undefined
  // entry throws inside render and the ErrorBoundary replaces the WHOLE screen
  // with an apology — the user loses the list, not just the row. That is what a
  // reducer bug in the vehicle slice did on 2026-08-24: it unshifted undefined
  // and the vehicles page went blank, while the record itself had saved fine.
  //
  // Dropping the bad entry is the honest degradation: the rest of the table is
  // real data and the user keeps working. Note this component renders every row
  // TWICE (desktop table + phone card), so an unguarded bad row throws twice.
  const data = Array.isArray(rawData) ? rawData.filter(Boolean) : [];

  if (!data.length) {
    return empty ? <Card padding="none">{empty}</Card> : null;
  }

  const primaryCols = columns.filter((c) => (c.priority ?? 1) === 1);
  const mobileCols = columns.filter((c) => (c.priority ?? 1) !== 3);
  const titleCol = primaryCols[0] || columns[0];
  const isDesktopOnly = (col) => col.priority === 3 && col.key !== 'actions';

  return (
    <div className={className}>
      {/* Desktop table — skipped entirely (not just CSS-hidden) under
          forceCards so narrow embedded hosts carry no dead table DOM. */}
      {!forceCards && (
      <Card padding="none" className="hidden md:block overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-2/70 border-b border-edge">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-4 py-3 font-semibold text-xs uppercase tracking-wide text-content-secondary whitespace-nowrap ${alignClass(col)} ${isDesktopOnly(col) ? 'hidden lg:table-cell' : ''}`}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-edge">
              {data.map((row, i) => (
                <tr
                  key={getKey(row, i)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={[
                    onRowClick ? 'cursor-pointer hover:bg-surface-2/60 transition-colors' : '',
                    rowExtra(row, i),
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-3.5 text-content ${alignClass(col)} ${col.numeric ? 'tabular-nums' : ''} ${isDesktopOnly(col) ? 'hidden lg:table-cell' : ''}`}
                    >
                      {cell(col, row, i)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {totalCols.length > 0 && (
              <tfoot>
                <tr className="bg-surface-2/70 border-t-2 border-edge font-bold">
                  {columns.map((col, idx) => (
                    <td
                      key={col.key}
                      className={`px-4 py-3 text-content ${alignClass(col)} ${col.numeric ? 'tabular-nums' : ''} ${isDesktopOnly(col) ? 'hidden lg:table-cell' : ''}`}
                    >
                      {hasTotal(col) ? totalCell(col) : idx === 0 ? totalsLabel : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {footer && <div className="border-t border-edge px-4 py-3">{footer}</div>}
      </Card>
      )}

      {/* Mobile card list. Title = cardTitle (or the first column); every other
          non-desktop-only column renders as a labelled value row, so a card is
          never blank even when columns carry no priority/cardLabel metadata. */}
      <div className={`${forceCards ? '' : 'md:hidden'} space-y-3`}>
        {data.map((row, i) => {
          const rowCols = mobileCols.filter(
            (col) => cardTitle || !titleCol || col.key !== titleCol.key
          );
          return (
            <Card
              key={getKey(row, i)}
              padding="sm"
              interactive={!!onRowClick}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={rowExtra(row, i)}
            >
              <div className="min-w-0 font-semibold text-content break-words mb-2">
                {cardTitle ? cardTitle(row) : cell(titleCol, row, i)}
              </div>
              {rowCols.length > 0 && (
                <dl className="space-y-1.5">
                  {rowCols.map((col) => (
                    <div key={col.key} className="flex items-baseline justify-between gap-3">
                      <dt className="text-xs text-content-muted shrink-0">
                        {col.cardLabel || col.header}
                      </dt>
                      <dd
                        className={`min-w-0 text-sm text-content text-end break-words ${
                          col.numeric ? 'tabular-nums' : ''
                        }`}
                      >
                        {cell(col, row, i)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {cardActions && (
                <div className="flex items-center justify-end gap-2 flex-wrap mt-3 pt-3 border-t border-edge">
                  {cardActions(row)}
                </div>
              )}
            </Card>
          );
        })}
        {totalCols.length > 0 && (
          <Card padding="sm" className="border-2 border-edge">
            <div className="font-bold text-content mb-2">{totalsLabel}</div>
            <dl className="space-y-1.5">
              {totalCols.map((col) => (
                <div key={col.key} className="flex items-baseline justify-between gap-3">
                  <dt className="text-xs text-content-muted shrink-0">{col.cardLabel || col.header}</dt>
                  <dd className={`min-w-0 text-sm font-bold text-content text-end ${col.numeric ? 'tabular-nums' : ''}`}>
                    {totalCell(col)}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
        )}
        {footer && <div className="pt-1">{footer}</div>}
      </div>
    </div>
  );
}
